// One campaign, up close: what it is, what it has actually done, and who is
// still on its list.
//
// Everything shown here is the engine's own account. Counts come from
// GET /{id}/statistics rather than the list DTO, because the list is a snapshot
// taken when the page loaded and this screen is watched while calls are placed.
//
// The decisions — which control is offered, whether a number is a measurement or
// an absence, whether a status is a failure — live in campaignDetailRules, away
// from the markup, where they are tested.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, RefreshCw, Play, Pause, Square, Users, AlertCircle, Loader2,
  ChevronLeft, ChevronRight, ChevronDown, Info, Pencil,
} from 'lucide-react';
import {
  voiceCampaignsService, VoiceCampaign, CampaignStatistics, CampaignEngineInfo, CallScheduleRow,
} from 'api/services/voiceCampaigns.service';
import QueueRecipientsModal from './QueueRecipientsModal';
import {
  LifecycleAction, lifecycleStates, normaliseStatus, PAUSE_CAVEAT, STOP_CAVEAT,
  formatCounter, formatSuccessRate, successRateNote, outcomeBreakdown, OutcomeKind,
  isEnforced, notEnforcedNote, recipientPage, pageSummary, outcomeLabel, outcomeKind,
  attemptHistoryOf, attemptsSummary, unrecordedNote, dialDetail, correctionNote,
} from './campaignDetailRules';
import {
  RetryForm, RETRY_FIELD, RETRY_OFF_FORM, RETRY_OFF_NOTE, RETRY_STRATEGIES,
  retryFormFrom, retryToApi, retryChanged, retryProblem, reportsRetry,
  attemptsNote, businessHoursNote, strategyHelp, strategyNote, retrySummary,
} from './campaignRetryRules';
import {
  FormValue, editableFieldsOf, canEdit, isEditable, lockedFields, lockedNote,
  buildPatch, isEmptyPatch, asInt, asText, toDateTimeLocal, isoFromLocal, failureMessage,
} from '../campaignEditRules';
import CallCategoryField, { traiComplianceOn } from 'components/Compliance/CallCategoryField';
import { useAuth } from 'hooks/useAuth';
import { CATEGORY_ADMINS, hasRole } from 'pages/Compliance/traiRules';

interface Props {
  /** Opaque. Decimal on cpass, hex on voicex — never coerced to a number. */
  campaignId: string;
  /** What the list already knew, so the header can paint before /{id} answers. */
  seed?: VoiceCampaign;
  onBack: () => void;
  /** Lets the list re-read its counters after a lifecycle change. */
  onChanged?: () => void;
}

/**
 * The design system's status palette, mapped to how an outcome should be read.
 *
 * `unreached` is deliberately amber and not the danger red used by `failed`:
 * NO_ANSWER and ABANDONED are not failures, and painting them the same colour
 * would undo in the eye what the grouping is there to keep apart.
 */
const KIND_TOKEN: Record<OutcomeKind, { bg: string; fg: string }> = {
  reached: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  failed: { bg: 'var(--danger-bg)', fg: 'var(--danger-text)' },
  unreached: { bg: 'var(--amber-tint)', fg: 'var(--amber-label)' },
  dialling: { bg: 'var(--info-bg)', fg: 'var(--info-text)' },
  waiting: { bg: 'var(--gray-bg)', fg: 'var(--gray-text)' },
  unknown: { bg: 'var(--gray-bg)', fg: 'var(--gray-text)' },
};

const statusBadge = (s: string) => {
  if (s === 'ACTIVE') return { background: 'var(--success-bg)', color: 'var(--success-text)' };
  if (s === 'PAUSED') return { background: 'var(--amber-tint)', color: 'var(--amber-label)' };
  if (s === 'COMPLETED') return { background: 'var(--info-bg)', color: 'var(--info-text)' };
  return { background: 'var(--gray-bg)', color: 'var(--gray-text)' };
};

/** The engine sends a local timestamp with no zone; an unparseable one is shown raw. */
const when = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const ACTIONS: { action: LifecycleAction; label: string; Icon: typeof Play; primary?: boolean }[] = [
  { action: 'start', label: 'Start', Icon: Play, primary: true },
  { action: 'pause', label: 'Pause', Icon: Pause },
  { action: 'resume', label: 'Resume', Icon: Play, primary: true },
  { action: 'stop', label: 'Stop', Icon: Square },
];

const PAGE_SIZE = 25;

type FieldKind = 'text' | 'textarea' | 'int' | 'bool' | 'datetime' | 'strategy';

/**
 * Every field this form knows how to draw, in the order it draws them.
 *
 * Which of them are actually drawn is the server's decision, arriving on the
 * campaign as `editableFields`; this table only says what each one looks like
 * and where its current value lives on the DTO — the campaign DTO and the
 * update body do not agree on every name (`defaultCallerId` is written back as
 * `callerId`, `primaryAgentId` as `aiAgentId`), so `read` is not `c[name]`.
 *
 * Calls per minute is first: it is the field somebody reaches for while the
 * campaign is dialling, and the alternative to finding it quickly is Pause.
 */
const EDIT_FIELDS: {
  name: string; label: string; kind: FieldKind; help?: string;
  read: (c: any) => FormValue;
}[] = [
  // Named as the API takes them — snake_case — because that is what
  // `editableFields` lists and what the body is read as. The campaign document
  // coming back is the runtime's, which spells the same things in camelCase,
  // so `read` bridges the two.
  { name: 'calls_per_minute', label: 'Calls per minute', kind: 'int', read: c => c?.callsPerMinute ?? '',
    help: 'Slows the dialler without stopping it. Applies to calls not yet placed.' },
  { name: 'name', label: 'Name', kind: 'text', read: c => c?.campaignName ?? c?.name ?? '' },
  { name: 'description', label: 'Description', kind: 'textarea', read: c => c?.description ?? '' },
  { name: 'max_concurrent_calls', label: 'Max concurrent calls', kind: 'int',
    read: c => c?.maxConcurrentCalls ?? '' },
  { name: 'max_call_delay', label: 'Max call delay (seconds)', kind: 'int', read: c => c?.maxCallDelay ?? '' },
  { name: 'end_date', label: 'Ends', kind: 'datetime', read: c => toDateTimeLocal(c?.endDate),
    help: 'Your local time. Stopping a campaign early is allowed while it runs.' },
  { name: 'start_date', label: 'Starts', kind: 'datetime', read: c => toDateTimeLocal(c?.startDate),
    help: 'Your local time.' },
  { name: 'campaign_type', label: 'Type', kind: 'text', read: c => c?.campaignType ?? '' },
  { name: 'caller_id', label: 'Caller ID', kind: 'text', read: c => c?.defaultCallerId ?? c?.callerId ?? '' },
  { name: 'agent_id', label: 'AI agent', kind: 'int', read: c => c?.agentId ?? '' },
  { name: 'welcome_message', label: 'Welcome message', kind: 'textarea', read: c => c?.welcomeMessage ?? '' },
  { name: 'voice_script_id', label: 'Voice script id', kind: 'text', read: c => c?.voiceScriptId ?? '' },
  { name: 'preferred_provider_id', label: 'Preferred provider', kind: 'text',
    read: c => c?.preferredProviderId ?? '' },
  { name: 'fallback_provider_id', label: 'Fallback provider', kind: 'text',
    read: c => c?.fallbackProviderId ?? '' },
];

const FIELD_LABEL: Record<string, string> = EDIT_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.name]: f.label }), { [RETRY_FIELD]: 'Retry settings' } as Record<string, string>,
);

/**
 * Everything the form can offer, retry included.
 *
 * Retry is not in EDIT_FIELDS because it is not a flat control — it is a nested
 * object with its own editor. It belongs in this list all the same: somebody
 * who opens Edit on a finished campaign and cannot find the retry settings
 * should be told they are fixed, not left to conclude the screen forgot them.
 */
const KNOWN_FIELDS = [...EDIT_FIELDS.map(f => f.name), RETRY_FIELD];

const INT_FIELDS = EDIT_FIELDS.filter(f => f.kind === 'int').map(f => f.name);
const DATE_FIELDS = EDIT_FIELDS.filter(f => f.kind === 'datetime').map(f => f.name);
/** Fields that exist to be emptied. Elsewhere, blank means "leave it alone". */
const CLEARABLE = ['description', 'welcome_message'];

/** The form as it stands when Edit opens: the campaign's current values. */
const formFromCampaign = (c: any): Record<string, FormValue> =>
  EDIT_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: f.read(c) }), {} as Record<string, FormValue>);

const toApi = (field: string, value: FormValue): any => {
  if (DATE_FIELDS.includes(field)) return isoFromLocal(value);
  if (INT_FIELDS.includes(field)) return asInt(value);
  if (typeof value === 'boolean') return value;
  if (CLEARABLE.includes(field)) return String(value ?? '').trim();
  return asText(value);
};

const STRATEGIES = ['FIXED', 'INCREMENTAL', 'EXPONENTIAL', 'CUSTOM'];

// The same control styling the campaign list's create modal uses, so the two
// forms are one form as far as the eye is concerned.
const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm';
const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

/**
 * The engine names the fields it ignores in snake_case (`calls_per_minute`);
 * the update body names them in camelCase. Without this the "stored but not
 * acted on" warning silently never fires — which is exactly the lie the warning
 * was added to prevent.
 */
const engineField = (name: string): string => name.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);

const CampaignDetail: React.FC<Props> = ({ campaignId, seed, onBack, onChanged }) => {
  const { currentOrganization, user } = useAuth();
  const categoryEditable = hasRole((currentOrganization?.role || user?.role)?.toLowerCase(), CATEGORY_ADMINS);
  const [campaign, setCampaign] = useState<VoiceCampaign | null>(seed || null);
  const [stats, setStats] = useState<CampaignStatistics | null>(null);
  const [statsNote, setStatsNote] = useState<string | null>(null);
  const [engine, setEngine] = useState<CampaignEngineInfo | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<LifecycleAction | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [queueing, setQueueing] = useState(false);

  // Editing. What may be changed is the server's answer (campaign.editableFields),
  // never a status table kept here — see ../campaignEditRules.
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, FormValue>>({});
  const [editInitial, setEditInitial] = useState<Record<string, FormValue>>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [editNote, setEditNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The retry block is held apart from editForm: it is one nested object the
  // server REPLACES rather than merges, so it is compared and sent whole.
  const [retryForm, setRetryForm] = useState<RetryForm>(RETRY_OFF_FORM);
  const [retryInitial, setRetryInitial] = useState<RetryForm>(RETRY_OFF_FORM);
  // The throttle on its own, kept apart from the modal's copy of the same field
  // so typing in one does not fight the other.
  const [rateDraft, setRateDraft] = useState('');
  const [rateBusy, setRateBusy] = useState(false);
  const [throttleNote, setThrottleNote] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [recipients, setRecipients] = useState<any>(null);
  const [recipientsBusy, setRecipientsBusy] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  /** Which recipient has its attempt history open. One at a time, by row id. */
  const [openAttempts, setOpenAttempts] = useState<string | null>(null);

  /**
   * The campaign and its statistics, together.
   *
   * Statistics are fetched even when the campaign read fails: the two come from
   * different sources, and a stale header is no reason to blank the counts.
   */
  const loadCampaign = useCallback(async () => {
    setError(null);
    const [c, s] = await Promise.all([
      voiceCampaignsService.get(campaignId),
      voiceCampaignsService.statistics(campaignId),
    ]);
    if (c.success) setCampaign(c.data as VoiceCampaign);
    else setError(c.error || 'Could not load this campaign');

    if (s.success) { setStats(s.data as CampaignStatistics); setStatsNote(null); }
    else { setStats(null); setStatsNote(s.error || 'The engine did not return statistics'); }
    setLoading(false);
  }, [campaignId]);

  const loadRecipients = useCallback(async (p: number) => {
    setRecipientsBusy(true); setRecipientsError(null);
    const res = await voiceCampaignsService.listRecipients(campaignId, p, PAGE_SIZE);
    if (res.success) setRecipients(res.data);
    else setRecipientsError(res.error || 'Could not load recipients');
    setRecipientsBusy(false);
  }, [campaignId]);

  useEffect(() => { loadCampaign(); }, [loadCampaign]);
  useEffect(() => { loadRecipients(page); }, [loadRecipients, page]);
  useEffect(() => {
    voiceCampaignsService.engine().then(r => { if (r.success) setEngine(r.data as CampaignEngineInfo); });
  }, []);

  // Keep the inline throttle showing the live value, except while it is being
  // typed into — overwriting a half-typed number mid-keystroke is maddening,
  // and this screen re-reads itself every ten seconds while dialling.
  const liveRate = campaign?.callsPerMinute;
  useEffect(() => {
    if (rateBusy) return;
    setRateDraft(liveRate === null || liveRate === undefined ? '' : String(liveRate));
    // Keyed on the server's value alone: a new value from the server replaces
    // the draft, a re-render does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRate]);

  const status = normaliseStatus(campaign?.status);
  const states = useMemo(() => lifecycleStates(status), [status]);

  // ── editing ───────────────────────────────────────────────────────────────
  const editable = editableFieldsOf(campaign);
  const rateEditable = isEditable(campaign, 'callsPerMinute');
  const shownFields = EDIT_FIELDS.filter(f => editable.includes(f.name));
  const retryEditable = editable.includes(RETRY_FIELD);
  const nothingToEdit = shownFields.length === 0 && !retryEditable;
  const whyLocked = lockedNote(
    campaign?.status,
    lockedFields(KNOWN_FIELDS, editable).map(n => FIELD_LABEL[n]),
  );
  const retryFault = retryEditable ? retryProblem(retryForm) : null;

  const openEdit = () => {
    const start = formFromCampaign(campaign);
    setEditForm(start);
    setEditInitial(start);
    const retry = retryFormFrom(campaign);
    setRetryForm(retry);
    setRetryInitial(retry);
    setEditError(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    setEditError(null);
    if (retryFault) { setEditError(retryFault); return; }
    const body = buildPatch(editInitial, editForm, editable, toApi);
    // Sent whole, or not at all. buildPatch is built for flat values — a nested
    // object compared field-by-field would send half a retry config, and the
    // server stores what it is handed rather than merging into what is there.
    // Off is an explicit null: the server reads absent/empty as "never re-dial".
    if (retryEditable && retryChanged(retryInitial, retryForm)) {
      body[RETRY_FIELD] = retryToApi(retryForm);
    }
    if (isEmptyPatch(body)) { setEditError('Nothing has been changed yet.'); return; }
    setSaving(true);
    const res = await voiceCampaignsService.update(campaignId, body);
    setSaving(false);
    // The server names the field it refused; replacing that with "Update failed"
    // throws away the only part of the answer worth reading.
    if (!res.success) { setEditError(failureMessage(res, 'Could not save those changes')); return; }
    setEditing(false);
    setEditNote(`Saved: ${Object.keys(body).map(k => FIELD_LABEL[k] || k).join(', ')}.`);
    await loadCampaign();
    onChanged?.();
  };

  /** The throttle alone — one number, one click, no modal. */
  const applyRate = async () => {
    setThrottleNote(null);
    const body = buildPatch({ callsPerMinute: liveRate }, { callsPerMinute: rateDraft },
                            ['calls_per_minute'], toApi);
    if (isEmptyPatch(body)) { setThrottleNote('That is already the rate.'); return; }
    setRateBusy(true);
    const res = await voiceCampaignsService.update(campaignId, body);
    setRateBusy(false);
    if (!res.success) { setThrottleNote(failureMessage(res, 'Could not change the rate')); return; }
    setThrottleNote(`Now dialling at ${body.callsPerMinute} a minute.`);
    await loadCampaign();
    onChanged?.();
  };

  /**
   * While it is dialling, the numbers on screen go stale by themselves.
   * Read-only, and only while there is something to watch.
   */
  useEffect(() => {
    if (status !== 'ACTIVE') return undefined;
    const t = setInterval(() => { loadCampaign(); loadRecipients(page); }, 10000);
    return () => clearInterval(t);
  }, [status, loadCampaign, loadRecipients, page]);

  const run = async (action: LifecycleAction) => {
    if (!states[action].enabled) return;
    setBusy(action); setError(null);
    const res = await voiceCampaignsService[action](campaignId);
    setBusy(null);
    setConfirmStop(false);
    if (!res.success) { setError(res.error || `Could not ${action} this campaign`); return; }
    await loadCampaign();
    await loadRecipients(page);
    onChanged?.();
  };

  const pageInfo = useMemo(() => recipientPage<CallScheduleRow>(recipients, PAGE_SIZE), [recipients]);
  const breakdown = useMemo(() => outcomeBreakdown(stats?.callsByStatus), [stats]);
  const breakdownTotal = breakdown.reduce((n, o) => n + o.count, 0);

  const rateNote = successRateNote(stats?.successRate, stats?.totalScheduledCalls);

  /** Config rows, each knowing whether this engine acts on it. */
  const config: { field: string; label: string; value: string }[] = [
    { field: 'agent_id', label: 'Agent', value: campaign?.primaryAgentId || '—' },
    { field: 'caller_id', label: 'Caller ID', value: campaign?.defaultCallerId || 'provider default' },
    { field: 'campaign_type', label: 'Type', value: campaign?.campaignType || '—' },
    { field: 'calls_per_minute', label: 'Calls per minute', value: formatCounter(campaign?.callsPerMinute) },
    { field: 'max_concurrent_calls', label: 'Max concurrent', value: formatCounter(campaign?.maxConcurrentCalls) },
    { field: 'max_call_delay', label: 'Max call delay', value: formatCounter(campaign?.maxCallDelay) },
    { field: 'start_date', label: 'Starts', value: when(campaign?.startDate) },
    { field: 'end_date', label: 'Ends', value: when(campaign?.endDate) },
  ];
  // Only when the campaign actually carried the block. The cpass engine's view
  // does not return it, and printing "off — never re-dialled" over a campaign
  // whose settings we were simply not told about would be a guess in the shape
  // of a fact.
  if (reportsRetry(campaign)) {
    config.push({ field: RETRY_FIELD, label: 'Retries', value: retrySummary(campaign) });
  }

  const tile = (label: string, value: string, note?: string | null) => (
    <div key={label} className="rounded-xl px-3 py-2.5" style={{ background: 'var(--surface-raised)' }}>
      <div className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-[19px] font-semibold mt-0.5" style={{ color: 'var(--text-primary)' }}>{value}</div>
      {note && <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{note}</div>}
    </div>
  );

  if (loading && !campaign) {
    return (
      <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading campaign…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button onClick={onBack} className="tl-btn tl-btn-secondary tl-btn-sm mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> All campaigns
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {campaign?.campaignName || '(unnamed)'}
            </h2>
            <span className="px-2 py-0.5 text-xs rounded-full" style={statusBadge(status)}>
              {status || 'UNKNOWN'}
            </span>
          </div>
          {campaign?.description && (
            <p className="text-[12.5px] mt-1" style={{ color: 'var(--text-muted)' }}>{campaign.description}</p>
          )}
          <p className="text-[11px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>
            id {campaignId}
            {engine?.engine ? ` · dialled by ${engine.dialledBy || engine.engine}` : ''}
            {campaign?.createdAt ? ` · created ${when(campaign.createdAt)}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { loadCampaign(); loadRecipients(page); }} className="tl-btn tl-btn-secondary tl-btn-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${recipientsBusy ? 'animate-spin' : ''}`} /> Refresh
          </button>
          {/* Only when the server says something can be changed. An absent or
              empty editableFields means no edit control at all. */}
          {canEdit(campaign) && (
            <button onClick={openEdit} className="tl-btn tl-btn-secondary tl-btn-sm">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          <button onClick={() => setQueueing(true)} className="tl-btn tl-btn-primary tl-btn-sm">
            <Users className="w-3.5 h-3.5" /> Queue recipients
          </button>
        </div>
      </div>

      {editNote && (
        <p className="text-[12.5px]" style={{ color: 'var(--success-text)' }}>{editNote}</p>
      )}

      {/* TRAI call category: behind the trai-compliance build flag, so a normal build is unchanged. */}
      {traiComplianceOn() && (
        <div className="tl-card">
          <CallCategoryField scope="campaign" id={campaignId} canEdit={categoryEditable} />
        </div>
      )}

      {/* The throttle, in the open.
          Slowing a dialler that is calling faster than anyone can answer is the
          edit people need in a hurry, and the control they reach for when they
          cannot find it is Pause — which stops the campaign instead of slowing
          it. So it sits beside the lifecycle buttons, not behind Edit. */}
      {rateEditable && (
        <div className="tl-card flex flex-wrap items-center gap-2">
          <label htmlFor="voice-rate" className="text-[12.5px] font-medium" style={{ color: 'var(--text-primary)' }}>
            Calls per minute
          </label>
          <input
            id="voice-rate"
            type="number"
            min={1}
            value={rateDraft}
            onChange={e => setRateDraft(e.target.value)}
            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
          />
          <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={rateBusy || rateDraft.trim() === ''}
                  onClick={applyRate}>
            {rateBusy ? 'Applying…' : 'Apply'}
          </button>
          {throttleNote && <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{throttleNote}</span>}
          {/* A rate this engine stores and does not honour is worse than no rate
              at all, so say which it is rather than implying a throttle. */}
          {!isEnforced(engine, engineField('callsPerMinute')) && (
            <span className="text-[11px] w-full" style={{ color: 'var(--amber-label)' }}>{notEnforcedNote(engine)}</span>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
             style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{error}
        </div>
      )}

      {/* Lifecycle. Every control is shown in every state; the ones the engine
          would refuse are disabled and say why, right underneath, rather than
          being offered and then rejected. */}
      <div className="tl-card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ACTIONS.map(({ action, label, Icon, primary }) => {
            const st = states[action];
            const isStop = action === 'stop';
            return (
              <div key={action}>
                <button
                  className={`tl-btn tl-btn-sm w-full ${primary ? 'tl-btn-primary' : 'tl-btn-secondary'}`}
                  disabled={!st.enabled || busy !== null}
                  onClick={() => (isStop ? setConfirmStop(true) : run(action))}
                >
                  {busy === action
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Icon className="w-3.5 h-3.5" />}
                  {label}
                </button>
                {!st.enabled && st.reason && (
                  <p className="text-[11px] mt-1 leading-snug" style={{ color: 'var(--text-muted)' }}>{st.reason}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* The one thing pause cannot do. Said where the button is, not in a
            tooltip, because it is the difference between "stopped" and
            "stopping". */}
        {states.pause.enabled && (
          <p className="text-[11.5px] mt-3 flex items-start gap-1.5" style={{ color: 'var(--text-muted)' }}>
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{PAUSE_CAVEAT}
          </p>
        )}

        {confirmStop && (
          <div className="mt-3 rounded-xl px-3 py-2.5"
               style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
            <p className="text-[12.5px]">{STOP_CAVEAT}</p>
            <div className="flex gap-2 mt-2">
              <button className="tl-btn tl-btn-neutral tl-btn-sm" onClick={() => setConfirmStop(false)}>Cancel</button>
              <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={busy !== null}
                      onClick={() => run('stop')}>
                {busy === 'stop' ? 'Stopping…' : 'Stop this campaign'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* What it has actually done. */}
      <div className="tl-card">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Calls</h3>
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {engine?.statisticsSource ? `counted by the engine from ${engine.statisticsSource}` : 'counted by the engine'}
          </span>
        </div>

        {statsNote ? (
          <p className="text-[12.5px] mt-2" style={{ color: 'var(--text-muted)' }}>{statsNote}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
              {tile('Queued total', formatCounter(stats?.totalScheduledCalls))}
              {tile('Answered', formatCounter(stats?.completedCalls),
                    'Reached the person and ran the call.')}
              {/* Only where the dialler actually distinguishes it. On the
                  voicex engine a ring-out is not a status of its own — see
                  VoiceCampaignService.normaliseStatistics — so the tile is
                  absent rather than showing a zero that would read as "nobody
                  missed a call". */}
              {stats?.noAnswerCalls !== undefined && stats?.noAnswerCalls !== null &&
                tile('Not answered', formatCounter(stats?.noAnswerCalls),
                     'Rang out, busy, or rejected. The call was placed.')}
              {tile('Failed', formatCounter(stats?.failedCalls),
                    'Could not be placed at all — a telephony fault, not a person.')}
              {tile('In progress', formatCounter(stats?.inProgressCalls))}
              {tile('Pending', formatCounter(stats?.pendingCalls))}
              {tile('Success rate', formatSuccessRate(stats?.successRate), rateNote)}
            </div>

            {breakdown.length > 0 && (
              <div className="mt-4">
                <div className="flex gap-[2px] h-2.5 rounded-full overflow-hidden" role="presentation">
                  {breakdown.map(o => (
                    <div key={o.status}
                         style={{
                           width: `${breakdownTotal ? (o.count / breakdownTotal) * 100 : 0}%`,
                           background: KIND_TOKEN[o.kind].fg,
                         }} />
                  ))}
                </div>
                {/* Label and count in ink beside a colour chip: the breakdown is
                    never carried by colour alone. */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2.5">
                  {breakdown.map(o => (
                    <span key={o.status} className="inline-flex items-center gap-1.5 text-[12px]"
                          style={{ color: 'var(--text-secondary)' }}>
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                            style={{ background: KIND_TOKEN[o.kind].fg }} />
                      {o.label}
                      <b style={{ color: 'var(--text-primary)' }}>{o.count.toLocaleString()}</b>
                    </span>
                  ))}
                </div>
                <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                  No answer and Abandoned are counted apart from Failed — a call nobody picked up, or one whose
                  turn never came, is not a call that went wrong.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Configuration, annotated with what this engine ignores. */}
      <div className="tl-card">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Configuration</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {config.map(({ field, label, value }) => {
            const enforced = isEnforced(engine, field);
            return (
              <div key={field} className="flex items-baseline justify-between gap-3 border-b pb-2"
                   style={{ borderColor: 'var(--divider)' }}>
                <dt className="text-[12.5px] flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{label}</dt>
                <dd className="text-[12.5px] text-right min-w-0"
                    style={{ color: enforced ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  <span className={enforced ? '' : 'line-through'}>{value}</span>
                  {!enforced && (
                    <span className="block text-[11px] mt-0.5" style={{ color: 'var(--amber-label)' }}>
                      {notEnforcedNote(engine)}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {/* Who is on the list, and what happened to each of them. */}
      <div className="tl-card">
        <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Recipients</h3>
          <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{pageSummary(pageInfo)}</span>
        </div>

        {recipientsError ? (
          <p className="text-[12.5px]" style={{ color: 'var(--danger-text)' }}>{recipientsError}</p>
        ) : pageInfo.rows.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-7 h-7 mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
            <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
              Nobody is queued yet. Queue recipients and this campaign has someone to call.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr style={{ color: 'var(--text-muted)' }}>
                  <th className="text-left font-medium pb-2">Number</th>
                  <th className="text-left font-medium pb-2">Outcome</th>
                  <th className="text-left font-medium pb-2">Attempts</th>
                  <th className="text-left font-medium pb-2">Dialled at</th>
                  <th className="text-left font-medium pb-2">From</th>
                  <th className="text-left font-medium pb-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {pageInfo.rows.map((r, i) => {
                  const kind = outcomeKind(r.callStatus);
                  const tok = KIND_TOKEN[kind];
                  const key = String(r.id || i);
                  const attempts = attemptHistoryOf(r);
                  const gap = unrecordedNote(r);
                  // Nothing to open when the engine recorded nothing and has
                  // nothing to admit — a chevron that expands to an empty box
                  // reads as a broken screen.
                  const expandable = attempts.length > 0 || gap !== null;
                  const open = openAttempts === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-t" style={{ borderColor: 'var(--divider)' }}>
                        <td className="py-2 pr-3 font-mono whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                          {r.callTo || '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <span className="px-2 py-0.5 rounded-full text-[11.5px] whitespace-nowrap"
                                style={{ background: tok.bg, color: tok.fg }}>
                            {outcomeLabel(r.callStatus)}
                          </span>
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                          {expandable ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                              style={{ color: 'var(--text-secondary)' }}
                              aria-expanded={open}
                              onClick={() => setOpenAttempts(open ? null : key)}
                            >
                              {attemptsSummary(r)}
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                            </button>
                          ) : (
                            formatCounter(r.retryCount)
                          )}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                          {when(r.callInitTime)}
                        </td>
                        <td className="py-2 pr-3 font-mono whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                          {r.callFrom || '—'}
                        </td>
                        <td className="py-2 max-w-[22rem]">
                          {r.lastErrorMessage
                            ? <span className="block truncate" title={r.lastErrorMessage}
                                    style={{ color: 'var(--danger-text)' }}>{r.lastErrorMessage}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                      </tr>

                      {/* Every dial of this number, in order. Inline rather than
                          in a dialog: it is read against the row above it. */}
                      {open && (
                        <tr style={{ background: 'var(--surface-raised)' }}>
                          <td colSpan={6} className="px-3 py-2.5">
                            {/* The counter and the history can disagree, and the
                                difference is real calls rather than a rounding
                                error. Said first, before the list it explains. */}
                            {gap && (
                              <p className="text-[11.5px] mb-2" style={{ color: 'var(--amber-label)' }}>{gap}</p>
                            )}
                            {attempts.length === 0 ? (
                              <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                                No attempt was recorded for this number.
                              </p>
                            ) : (
                              <ol className="space-y-1.5">
                                {attempts.map((a, n) => {
                                  const aKind = outcomeKind(a.outcome);
                                  const aTok = KIND_TOKEN[aKind];
                                  const raw = dialDetail(a);
                                  return (
                                    <li key={a.attemptNo ?? n}
                                        className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[12px]">
                                      <span className="font-medium tabular-nums"
                                            style={{ color: 'var(--text-muted)' }}>
                                        #{a.attemptNo ?? n + 1}
                                      </span>
                                      <span className="whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                                        {when(a.startedAt)}
                                      </span>
                                      {/* The campaign's own word leads; the
                                          switch's word is detail beside it. */}
                                      <span className="px-2 py-0.5 rounded-full text-[11px] whitespace-nowrap"
                                            style={{ background: aTok.bg, color: aTok.fg }}>
                                        {outcomeLabel(a.outcome)}
                                      </span>
                                      {raw && (
                                        <span className="font-mono text-[11px]" style={{ color: 'var(--text-muted)' }}>
                                          {raw}
                                        </span>
                                      )}
                                      {a.lastError && (
                                        <span className="text-[11.5px]" style={{ color: 'var(--danger-text)' }}>
                                          {a.lastError}
                                        </span>
                                      )}
                                      {correctionNote(a) && (
                                        <span className="text-[11px] italic"
                                              title={`Corrected ${when(a.correctedAt)}`}
                                              style={{ color: 'var(--text-muted)' }}>
                                          {correctionNote(a)}
                                        </span>
                                      )}
                                      {a.providerCallId && (
                                        <span className="font-mono text-[10.5px]"
                                              style={{ color: 'var(--text-muted)' }}>
                                          {a.providerCallId}
                                        </span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(pageInfo.page > 0 || !pageInfo.last) && (
          <div className="flex items-center justify-between gap-2 mt-3">
            <button className="tl-btn tl-btn-secondary tl-btn-sm"
                    disabled={pageInfo.first || recipientsBusy}
                    onClick={() => setPage(p => Math.max(0, p - 1))}>
              <ChevronLeft className="w-3.5 h-3.5" /> Previous
            </button>
            <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
              Page {pageInfo.page + 1}{pageInfo.totalPages !== null ? ` of ${pageInfo.totalPages}` : ''}
            </span>
            <button className="tl-btn tl-btn-secondary tl-btn-sm"
                    disabled={pageInfo.last || recipientsBusy}
                    onClick={() => setPage(p => p + 1)}>
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Edit modal — the same shape as the create modal on the campaign list, so
          this page does not introduce a second kind of dialog. */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Edit campaign</h2>
            <p className="text-[12.5px] mb-4" style={{ color: 'var(--text-muted)' }}>
              {status || 'UNKNOWN'} · only what you change is sent.
            </p>

            <div className="space-y-3">
              {shownFields.map(f => (
                <div key={f.name}>
                  {f.kind === 'bool' ? (
                    <label className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <input
                        type="checkbox"
                        checked={editForm[f.name] === true}
                        onChange={e => setEditForm({ ...editForm, [f.name]: e.target.checked })}
                      />
                      {f.label}
                    </label>
                  ) : (
                    <>
                      <label className={lbl}>{f.label}</label>
                      {f.kind === 'textarea' ? (
                        <textarea
                          rows={2}
                          className={input}
                          value={String(editForm[f.name] ?? '')}
                          onChange={e => setEditForm({ ...editForm, [f.name]: e.target.value })}
                        />
                      ) : f.kind === 'strategy' ? (
                        <select
                          className={input}
                          value={String(editForm[f.name] ?? '')}
                          onChange={e => setEditForm({ ...editForm, [f.name]: e.target.value })}
                        >
                          <option value="">— unchanged —</option>
                          {STRATEGIES.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : (
                        <input
                          type={f.kind === 'int' ? 'number' : f.kind === 'datetime' ? 'datetime-local' : 'text'}
                          min={f.kind === 'int' ? 0 : undefined}
                          className={input}
                          value={String(editForm[f.name] ?? '')}
                          onChange={e => setEditForm({ ...editForm, [f.name]: e.target.value })}
                        />
                      )}
                    </>
                  )}
                  {f.help && <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{f.help}</p>}
                  {/* The same annotation the Configuration panel carries: a field
                      this engine stores and ignores is worth editing only if you
                      know it will not change what gets dialled. */}
                  {!isEnforced(engine, engineField(f.name)) && (
                    <p className="text-[11px] mt-1" style={{ color: 'var(--amber-label)' }}>{notEnforcedNote(engine)}</p>
                  )}
                </div>
              ))}

              {/* Retry settings.
                  Offered only when the server lists retry_configuration. Kept
                  visibly apart from the flat fields because it is one object
                  that is replaced whole — and because its first control is not
                  a value but a consequence: a campaign with no retry settings
                  never re-dials anybody. */}
              {retryEditable && (
                <div className="pt-3 mt-1 border-t" style={{ borderColor: 'var(--divider)' }}>
                  <label className="inline-flex items-center gap-2 text-sm font-medium"
                         style={{ color: 'var(--text-primary)' }}>
                    <input
                      type="checkbox"
                      checked={retryForm.on}
                      onChange={e => setRetryForm({ ...retryForm, on: e.target.checked })}
                    />
                    Retry calls nobody answered
                  </label>

                  {!retryForm.on ? (
                    <p className="text-[11.5px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                      {RETRY_OFF_NOTE}
                    </p>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className={lbl} htmlFor="retry-attempts">Total attempts</label>
                        <input
                          id="retry-attempts"
                          type="number"
                          min={1}
                          className={input}
                          value={retryForm.maxAttempts}
                          onChange={e => setRetryForm({ ...retryForm, maxAttempts: e.target.value })}
                        />
                        {/* The field people read backwards. Said as a count of
                            rings, not as a count of tries. */}
                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                          {attemptsNote(retryForm.maxAttempts)}
                        </p>
                      </div>

                      <div>
                        <label className={lbl} htmlFor="retry-delay">Wait before the next try (minutes)</label>
                        <input
                          id="retry-delay"
                          type="number"
                          min={1}
                          className={input}
                          value={retryForm.baseDelayMinutes}
                          onChange={e => setRetryForm({ ...retryForm, baseDelayMinutes: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className={lbl} htmlFor="retry-strategy">How the wait grows</label>
                        <select
                          id="retry-strategy"
                          className={input}
                          value={retryForm.strategy}
                          onChange={e => setRetryForm({
                            ...retryForm,
                            strategy: e.target.value === 'EXPONENTIAL' ? 'EXPONENTIAL' : 'FIXED',
                          })}
                        >
                          {RETRY_STRATEGIES.map(v => (
                            <option key={v} value={v}>
                              {v === 'FIXED' ? 'Fixed — the same wait every time'
                                             : 'Exponential — each wait longer than the last'}
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                          {strategyHelp(retryForm)}
                        </p>
                      </div>

                      {/* Absent under FIXED rather than disabled: the delay maths
                          does not read either of them, and a greyed box still
                          invites somebody to wonder what it would do. */}
                      {retryForm.strategy === 'EXPONENTIAL' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={lbl} htmlFor="retry-backoff">Backoff factor</label>
                            <input
                              id="retry-backoff"
                              type="number"
                              min={1}
                              step="0.5"
                              className={input}
                              value={retryForm.backoffFactor}
                              onChange={e => setRetryForm({ ...retryForm, backoffFactor: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className={lbl} htmlFor="retry-cap">Longest wait (minutes)</label>
                            <input
                              id="retry-cap"
                              type="number"
                              min={1}
                              className={input}
                              value={retryForm.maxDelayMinutes}
                              onChange={e => setRetryForm({ ...retryForm, maxDelayMinutes: e.target.value })}
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="inline-flex items-center gap-2 text-sm"
                               style={{ color: 'var(--text-primary)' }}>
                          <input
                            type="checkbox"
                            checked={retryForm.respectBusinessHours}
                            onChange={e => setRetryForm({
                              ...retryForm, respectBusinessHours: e.target.checked,
                            })}
                          />
                          Only retry during calling hours
                        </label>
                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                          {businessHoursNote(retryForm.respectBusinessHours)} It is on unless it is
                          turned off here.
                        </p>
                      </div>

                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        Saved as one set: everything above is sent together and replaces whatever was
                        stored, rather than being merged into it.
                      </p>
                    </div>
                  )}

                  {/* A strategy this form cannot draw would otherwise be
                      flattened to FIXED on save, silently. */}
                  {strategyNote(campaign) && (
                    <p className="text-[11px] mt-2" style={{ color: 'var(--amber-label)' }}>
                      {strategyNote(campaign)}
                    </p>
                  )}

                  {/* "Off" from a campaign that never told us is a different
                      statement from "off" it reported. */}
                  {!reportsRetry(campaign) && (
                    <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                      This campaign did not report any saved retry settings, so what is shown here is
                      what will be sent — not necessarily what is stored now.
                    </p>
                  )}

                  {retryFault && (
                    <p className="text-[11.5px] mt-2" style={{ color: 'var(--danger-text)' }}>{retryFault}</p>
                  )}
                </div>
              )}

              {nothingToEdit && (
                <p className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                  The server offers no fields this screen knows how to edit.
                </p>
              )}

              {/* Somebody who came to change the agent and cannot find the field
                  should not have to work out whether it is hidden or broken. */}
              {whyLocked && (
                <p className="text-[11.5px] pt-2 border-t" style={{ color: 'var(--text-muted)', borderColor: 'var(--divider)' }}>
                  {whyLocked}
                </p>
              )}

              {editError && (
                <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
                     style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{editError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setEditing(false)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={saveEdit}
                      disabled={saving || nothingToEdit || retryFault !== null}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {queueing && campaign && (
        <QueueRecipientsModal
          campaign={campaign}
          onClose={() => setQueueing(false)}
          onQueued={() => { loadCampaign(); loadRecipients(page); onChanged?.(); }}
          inputClass="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm"
          labelClass="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
        />
      )}
    </div>
  );
};

export default CampaignDetail;
